import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import "./CountryBalance.css";

const normalizeId = (id) => {
  if (id === undefined || id === null) return "";
  return String(id);
};

const fmtMoney = (n, currency = "USD") =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(n || 0));

export default function AgentBalanceList({ agents, onRefund, onReturn, eligibleAgentIds }) {
  const navigate = useNavigate();
  const eligibleSet = eligibleAgentIds instanceof Set ? eligibleAgentIds : null;

  if (!agents || agents.length === 0) {
    return <p className="text-body-secondary fst-italic">No agents found for this country.</p>;
  }

  return (
    <div className="row g-2">
      {agents.map((agent) => {
        const available = Number(agent.available ?? agent.balance ?? 0);
        const reserved = Number(agent.reserved ?? 0);
        const received = Number(agent.received ?? 0);
        const currency = (agent.currency || "USD").toUpperCase();
        const canAdjust = eligibleSet ? eligibleSet.has(normalizeId(agent.id)) : true;

        const totalBase = Math.max(available + reserved, received, 1);
        const reservedPct = Math.min(100, Math.max(0, (reserved / totalBase) * 100));

        const itemKey = agent.key || `${agent.id}-${currency}`;

        return (
          <div key={itemKey} className="col-12 col-sm-6">
            <div className="h-100 p-3 border rounded-4 bg-body">
              <div className="d-flex align-items-center gap-3 mb-2">
                <div
                  className="rounded-circle d-inline-flex align-items-center justify-content-center bg-body-tertiary border"
                  style={{ width: 40, height: 40 }}
                  title={agent.name}
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    className="text-primary"
                  >
                    <circle cx="12" cy="8" r="4" />
                    <path d="M6 20c0-3.314 2.686-6 6-6s6 2.686 6 6" />
                  </svg>
                </div>

                <div className="flex-grow-1 min-w-0">
                  <div
                    className="fw-semibold text-truncate agent-name"
                    title={agent.name}
                    role="button"
                    tabIndex={0}
                    style={{ cursor: "pointer" }}
                    onClick={() => navigate(`/home/agent/${agent.uuid}`)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") navigate(`/home/agent/${agent.uuid}`);
                    }}
                  >
                    {agent.name}
                  </div>

                  <div className="text-body-secondary small">
                    Available: <strong>{fmtMoney(available, currency)}</strong>
                  </div>
                </div>

                <span className="badge rounded-pill bg-body border text-body">{currency}</span>
              </div>

              {(received > 0 || reserved > 0) && (
                <div className="mt-2">
                  {received > 0 && (
                    <div className="d-flex justify-content-between small mb-1">
                      <span className="text-body-secondary">Received</span>
                      <strong>{fmtMoney(received, currency)}</strong>
                    </div>
                  )}

                  {reserved > 0 && (
                    <>
                      <div className="d-flex justify-content-between small text-body-secondary">
                        <span>Reserved</span>
                        <span>{fmtMoney(reserved, currency)}</span>
                      </div>
                      <div
                        className="progress"
                        role="progressbar"
                        aria-valuenow={reservedPct}
                        aria-valuemin="0"
                        aria-valuemax="100"
                        style={{ height: 6 }}
                      >
                        <div className="progress-bar bg-danger" style={{ width: `${reservedPct}%` }} />
                      </div>
                    </>
                  )}
                </div>
              )}

              {canAdjust && (typeof onRefund === "function" || typeof onReturn === "function") && (
                <div className="mt-3 d-flex flex-wrap gap-2 justify-content-end">
                  {typeof onRefund === "function" && (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary rounded-pill"
                      onClick={() => onRefund(agent)}
                    >
                      Refund
                    </button>
                  )}
                  {typeof onReturn === "function" && (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary rounded-pill"
                      onClick={() => onReturn(agent)}
                    >
                      Agent Return
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

AgentBalanceList.propTypes = {
  agents: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
      uuid: PropTypes.string,
      name: PropTypes.string.isRequired,
      available: PropTypes.number,
      balance: PropTypes.number,
      reserved: PropTypes.number,
      received: PropTypes.number,
      currency: PropTypes.string.isRequired,
      countryCode: PropTypes.string,
      countryName: PropTypes.string,
      key: PropTypes.string,
    })
  ).isRequired,
  onRefund: PropTypes.func,
  onReturn: PropTypes.func,
  eligibleAgentIds: PropTypes.instanceOf(Set),
};
